import React, { useContext } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { Label } from '@nooz-optics/nooz-components-lib';
import { IButton } from '../../../../../utils/types/contentful';
import { CheckoutContext } from '../../../utils/providers/checkout';
import { CartContext } from '../../../utils/providers/cart';
import { Cart } from '../Cart/cart';
import { CartFooter } from '../Cart/Footer/cartFooter';
import { IColor } from '../../../utils/types/contentful';
import { formatContentfulComponentProps } from '../../../utils/globals';
import { modalState } from '../../../utils/recoil/modal';
import { pushAddToCartToDataLayer } from '../../../utils/dataLayer/addToCart';
import { shopifyVariantsState } from '../../../utils/recoil/product';
import { SplitMediaCrmFormPanel } from '../../Marketing/Panel/Split/ImageBody/splitImageBodyPanel';
import { withSuspense } from '../../../utils/hocs/withSuspense';
import { Button } from '../../UI/Elements';
import { useDrawer } from 'utils/recoil/drawer';

export interface BuyProductButton {
  buttons: IButton;
  color: IColor;
  currentVariantSelection: any;
}

const LoadingButton = () => (
  <Button color={{ classname: 'primary' }} fullWidth disabled type='contained'>
    <div className='flex justify-center items-center h-full w-full'>
      <svg
        className='animate-spin -ml-1 mr-3 h-5 w-5 text-white'
        xmlns='http://www.w3.org/2000/svg'
        fill='none'
        viewBox='0 0 24 24'
      >
        <circle
          className='opacity-25'
          cx='12'
          cy='12'
          r='10'
          stroke='currentColor'
          strokeWidth='4'
        />
        <path
          className='opacity-75'
          fill='currentColor'
          d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
        />
      </svg>
    </div>
  </Button>
);

export const BuyProductButton: React.FC<BuyButtonProps> = withSuspense(
  ({ buttons, color }) => {
    const drawer = useDrawer();
    const [modal, setModal] = useRecoilState(modalState);
    const shopifyVariants = useRecoilValue(shopifyVariantsState);

    const cart = useContext(CartContext);
    const body = formatContentfulComponentProps(cart.body);
    const footer = formatContentfulComponentProps(cart.footer);
    const { client, checkout, updateCheckout, loading } = useContext(
      CheckoutContext
    );

    const fullySelectedVariant = shopifyVariants?.length === 1;
    const partiallySelectedVariant = shopifyVariants?.length > 1;
    const isSelectedVariantAvailable =
      shopifyVariants && shopifyVariants[0]?.availableForSale;

    const returnBuyButtonColor = !color ? { classname: 'primary' } : color;

    const BaseButton = (props) => (
      <Button
        label={{
          text: buttons?.chooseVariant?.text,
          weight: 'normal',
        }}
        fullWidth
        type='contained'
        size='base'
        color={returnBuyButtonColor}
        {...props}
      />
    );

    const handleAddToCartClick = () => {
      // Open Cart sidebar
      drawer.open({
        ...cart,
        body: <Cart body={body} promotion={cart.promotion} />,
        footer: <CartFooter body={body} footer={footer} />,
        placement: 'right',
        width: 'lg:w-screen/2 sm:w-screen-2/3 w-screen-95',
      });

      if (typeof window !== 'undefined' && shopifyVariants?.length === 1) {
        const variantToAdd = shopifyVariants[0];
        // Start cart loader
        loading[1](true);

        // Take the first and only selected variant
        const graphqlApiId = window.btoa(variantToAdd.id);
        const lineItemsToAdd = [
          {
            variantId: graphqlApiId,
            quantity: 1,
          },
        ];

        // Add line item to the shopify checkout
        client.checkout
          .addLineItems(checkout[0].id, lineItemsToAdd)
          .then((newCheckout) => {
            // Push product to data layer add to cart event
            pushAddToCartToDataLayer(variantToAdd);
            // Update Checkout with updated checkout object
            updateCheckout(newCheckout);
          });
      }
    };

    const handleBackInStockClick = () => {
      setModal({
        ...modal,
        show: true,
        hasOverlay: true,
        children: (
          <SplitMediaCrmFormPanel
            {...formatContentfulComponentProps(buttons.modal)}
          />
        ),
        placement: 'center',
      });
    };

    const returnBuyButtonComponent = () => {
      if (partiallySelectedVariant) {
        // Has more than one variant: can't add to cart
        return <BaseButton disabled />;
      } else if (fullySelectedVariant) {
        // Has a variant to add to cart
        if (isSelectedVariantAvailable) {
          // variant is available for sale
          return (
            <BaseButton
              label={{
                text: buttons?.addToCartButton?.text,
                weight: 'normal',
              }}
              icon={{ iconKey: 'HiChevronRight', color: 'white' }}
              onClick={handleAddToCartClick}
            />
          );
        }
        if (!isSelectedVariantAvailable) {
          // variant is out of stock
          return (
            <BaseButton
              label={{
                text: buttons?.notifyMeLabel?.text,
                weight: 'normal',
              }}
              disabled
              // onClick={handleBackInStockClick} TODO ACTIVATE BACK IN STOCK POPUP
            />
          );
        }
      } else {
        return <></>;
      }
    };

    return (
      <>
        {returnBuyButtonComponent()}
        <div className='w-full flex justify-center mt-1'>
          <Label {...buttons.subLabel} size='xs' />
        </div>
      </>
    );
  },
  <LoadingButton />
);
